/**
 * Production adapter that wires the React-free ports
 * ({@link SymmetricMarginDeps}) to the live engine and the structure-creation
 * choreography. The GPU/engine is a SYSTEM BOUNDARY: this glue is verified by
 * the manual demo + the mandatory HITL human safety review for this slice
 * (per the locked test-depth decision), not by unit tests — the unit-tested
 * safety core consumes these ports through deterministic fakes.
 *
 * Geometry uses the SAME proven engine entry point as the toolbar
 * (`SDFOperations.applySymmetricMargin`), so the agent path cannot diverge
 * geometrically. The shared overlay/release/create wiring lives in
 * {@link makeEngineDeps}; this file holds only the op-specific apply closure
 * and the undo-history label.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import type { Environment } from '~/engine/data/Environment';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { useApplyComputedGeometry } from '~/hooks/structure-set/useApplyComputedGeometry';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    makeEngineDeps,
    requireSourceTexture,
    toComputedGeometry,
} from '../core';
import type { ComputedGeometry, Mm } from '../core';
import type { SymmetricMarginDeps } from './pipeline';

/**
 * Returns a factory producing {@link SymmetricMarginDeps} bound to the engine
 * state. Handles are captured at render time because `getOrCreateDestination`
 * needs the live environment.
 */
export function useMarginEngineDeps(): () => SymmetricMarginDeps {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const applyGeometry = useApplyComputedGeometry();

    return useCallback(
        (): SymmetricMarginDeps =>
            makeEngineDeps(env, structureSet, applyGeometry, {
                label: 'Symmetric Margin',
                geometry: (env: Environment | null) => ({
                    async applySymmetricMargin(
                        sourceRoiNumber: number,
                        marginMm: Mm
                    ): Promise<ComputedGeometry> {
                        const src = requireSourceTexture(env, sourceRoiNumber);
                        const result = await SDFOperations.applySymmetricMargin(
                            src,
                            marginMm
                        );
                        return toComputedGeometry(result);
                    },
                }),
            }),
        [env, structureSet, applyGeometry]
    );
}
